package Structs

import DataBase.UINKDBInterface
import Utils.DocType
import Utils.OrderStatus
import kotlinx.serialization.SerialInfo
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import myName

private fun meaningfulDeliveryDate(raw: String?): String? {
    val date = raw?.trim()?.substringBefore(' ')?.trim()
    return if (date.isNullOrEmpty() || date == "null" || date == "0000-00-00") null else date
}

@myName("Note")
@Serializable
sealed class Note(

) : InformationBase() {
    abstract val date: String
    abstract val date_issued: String
    abstract val agent: String
    abstract val id: Int
    abstract val ent_id: Int
    abstract var active: Int
    abstract var delivery_value: String
    abstract var connected_id: Long
    abstract var notes: String
    abstract var agent_id: Int
    abstract var notes2: String
    abstract var notes3: String
    abstract var notes4: String
    abstract var notes5: String
    abstract var history: String
    abstract var action_time: String
    abstract var driver_id: Int?
    abstract var collector_id: Int?

    @myName("increase_id")
    abstract var increase_id: Int?
    var value: Float = 0f
    var taxPaid: Float = 0f
    var taxPaidPos: Float = 0f
    var taxPaidNeg: Float = 0f
    var amount_pos: Float = 0f
    var amount_pos_sec: Float = 0f
    var amount_neg: Float = 0f
    var amount_neg_sec: Float = 0f
    var value_pos: Float = 0f
    var value_neg: Float = 0f
    var discount: Float = 0f
    var discountTax: Float = 0f

    var delivery_info: MutableList<ProductDelivery> = mutableListOf()
    var histroy_info: MutableList<ProductDelivery> = mutableListOf()

    override fun getAmounts(): Float {
        return delivery_info.sumOf { (it.value + (it.wrapped_amount) * it.conversion_ratio).toDouble() }
            .toFloat()
    }

    override fun getConnectedProductsDelivery(): List<ProductDelivery> {
        return delivery_info
    }

    override fun getAmountsSecond(): Float {
        return delivery_info.sumOf {
            val conversion = it.conversionForSecondAmount()
            if (conversion != 0f && it.value > 0) {
                (it.value / conversion + it.wrapped_amount).toDouble()
            } else
                (it.wrapped_amount).toDouble()
        }
            .toFloat()
    }

    override fun getReturnAmounts(): Float {
        return delivery_info.sumOf { (it.returns + (it.conversion_ratio) * it.wrapped_amount_return).toDouble() }
            .toFloat()
    }

    override fun getConnectedDate(): String {
        return date
    }

    override fun getConnectedName(): String {
        return UINKDBInterface.activeDB.getClient(ent_id).first?.getName() ?: ""
    }

    override fun getConnectedAgent(): String {
        return agent
    }

    override fun getNotesComment2(): String {
        return notes2
    }

    override fun getNotesComment3(): String {
        return notes3
    }

    override fun getNotesComment4(): String {
        return  notes4
    }

    override fun getNotesComment5(): String {
        return notes5
    }
    override fun getComments(): String {
        return notes
    }

    override fun getConnectedId(): Int {
        return id
    }

    override fun getConnectedDateTime(): String {
        return date_issued
    }

    override fun getConnectedValue(): Float {
        return value
    }

    override fun isActive(): Boolean {
        return active == 1
    }

    override fun isUsed(): Boolean {
        return false
    }

    override fun toString(): String {
        return date.toString()
    }

    override fun getConnectedEntId(): Int {
        return ent_id
    }

    open fun getEnt(client: Boolean = true): Entity {
        return EmptyEnt()
    }

    /** Forced document fields (business name / id / address / phone) that override the client when present. */
    @myName("getStaticData")
    open fun getStaticData(): ClientStaticData? = null

    /** True when this note carries forced fields that should replace the client values in the document. */
    @myName("hasStaticData")
    fun hasStaticData(): Boolean = getStaticData() != null

    /** Address to print: forced static address when non-empty, otherwise the client address. */
    @myName("effectiveAddress")
    fun effectiveAddress(fallback: String): String {
        val forced = getStaticData()?.address
        return if (!forced.isNullOrEmpty()) forced else fallback
    }

    /** Business name to print: forced static business name when non-empty, otherwise the fallback (client). */
    @myName("effectiveBusinessName")
    fun effectiveBusinessName(fallback: String): String {
        val forced = getStaticData()?.business_name
        return if (!forced.isNullOrEmpty()) forced else fallback
    }

    /** Business id to print: forced static business id when non-empty, otherwise the fallback (client). */
    @myName("effectiveBusinessId")
    fun effectiveBusinessId(fallback: String): String {
        val forced = getStaticData()?.business_id
        return if (!forced.isNullOrEmpty()) forced else fallback
    }

    /** Phone to print: forced static phone when non-empty, otherwise the fallback (client). */
    @myName("effectivePhone")
    fun effectivePhone(fallback: String): String {
        val forced = getStaticData()?.phone
        return if (!forced.isNullOrEmpty()) forced else fallback
    }

    override fun getConnectedTaxValueReal(): Float {
        return taxPaid
    }

    override fun getBeforeValueReal(): Float {
        return return value - taxPaid
    }

    /** Total product cost for profit (same formula as ClientTaxNote.getCostProducts). */
    @myName("getCostProducts")
    fun getCostProducts(): Float {
        return delivery_info.sumOf {
            val product = it.getProduct()
            val cost_returns = product.cost_include_returns
            val price = it.cost
                ?: UINKDBInterface.activeDB.getProductCost(it.productId)?.get(date)?.first
                ?: 0f
            val qty = it.value + it.wrapped_amount * it.conversion_ratio
            val returns = it.returns + it.wrapped_amount_return * it.conversion_ratio
            val costQty = qty + (1 - cost_returns) * returns
            (costQty * price).toDouble()
        }.toFloat()
    }

    @myName("isOnlyReturns")
    fun isOnlyReturns(): Boolean {
        return delivery_info.all { it.value == 0f && it.wrapped_amount == 0f && (it.returns > 0 || it.wrapped_amount_return > 0) }
    }

    @myName("hasExtraUnit")
    fun hasExtraUnit(): Boolean {
        return delivery_info.any { it.wrapped_amount > 0f || it.wrapped_amount_return > 0 }
    }

    @myName("getAgentId")
    fun getAgentId(): Int? {
        return UINKDBInterface.activeDB.getAgentBy(name = agent, id = null, force = true).first?.id
    }

    @myName("hasReturns")
    fun hasReturns(): Boolean {
        return delivery_info.any { (it.returns > 0 || it.wrapped_amount_return > 0) }
    }

    @myName("hasValues")
    fun hasValues(): Boolean {
        return delivery_info.any { (it.value > 0 || it.wrapped_amount > 0) }
    }

    override fun getConnectedProductsIds(): List<Int> {
        return delivery_info.map { it.productId }
    }

    enum class NoteType(val size: Int) {
        VALUES(0),
        RETURNS(1),
        BOTH(2);

        fun convert(size: Int): NoteType {
            return when (size) {
                0 -> VALUES
                1 -> RETURNS
                else -> BOTH
            }
        }
    }


    companion object {
        fun sql(delivery_info: List<ProductDelivery>): String {
            var s: StringBuilder = StringBuilder()
            for (element in delivery_info) {
                s.append(element.sql())
            }
            return s.toString()
        }

        fun realSizeSplitReturns(l: List<Note>): Int {
            return l.sumBy { it.splittedSize() }
        }

        fun getAllProducts(l: List<Note>): Set<Int> {
            val v = mutableSetOf<Int>()
            l.forEach {
                v.addAll(it.delivery_info.map { it.productId })
                v.addAll(it.histroy_info.map { it.productId })
                if(it is OrderNote){
                    v.addAll(it.freeze_info.map { it.productId })
                }

            }
            return v
        }

        /** When false, line price is taken from JSON (p / u / utn+paid), not client_prices ladder. */
        fun lineNeedsClientPriceLadder(pd: ProductDelivery, isOrder: Boolean, deliveryPaid: Int): Boolean {
            if (isOrder) {
//                if (pd.price > 0f) return false
                if (pd.use_price > 0.toByte() || pd.use_price_tax_note > 0.toByte()) return false
                return true
            }
            if (pd.use_price >= 1.toByte()) return false
            if (deliveryPaid == 1 && pd.use_price_tax_note >= 1.toByte()) return false
            return true
        }

        fun priceTupleCsvForOrderNotes(notes: List<OrderNote>): String {
            val keys = linkedSetOf<Pair<Int, Int>>()
            for (n in notes) {
                val c = UINKDBInterface.activeDB.getClient(n.ent_id).first ?: continue
                val lid = c.priceListIdForClientPricesFetch()
                for (pd in n.delivery_info) {
                    if (!lineNeedsClientPriceLadder(pd, isOrder = true, deliveryPaid = 0)) continue
                    if (pd.productId <= 0) continue
                    keys.add(lid to pd.productId)
                }
            }
            return keys.joinToString(",") { "${it.first}:${it.second}" }
        }

        fun priceTupleCsvForDeliveryNotes(notes: List<DeliveryNote>): String {
            val keys = linkedSetOf<Pair<Int, Int>>()
            for (n in notes) {
                val c = UINKDBInterface.activeDB.getClient(n.ent_id).first ?: continue
                val lid = c.priceListIdForClientPricesFetch()
                for (pd in n.delivery_info) {
                    if (!lineNeedsClientPriceLadder(pd, isOrder = false, deliveryPaid = n.paid)) continue
                    if (pd.productId <= 0) continue
                    keys.add(lid to pd.productId)
                }
            }
            return keys.joinToString(",") { "${it.first}:${it.second}" }
        }

        fun priceTupleCsvForMixedDocs(notes: List<InformationBase>): String {
            val keys = linkedSetOf<Pair<Int, Int>>()
            notes.filterIsInstance<OrderNote>().forEach { n ->
                val c = UINKDBInterface.activeDB.getClient(n.ent_id).first ?: return@forEach
                val lid = c.priceListIdForClientPricesFetch()
                for (pd in n.delivery_info) {
                    if (!lineNeedsClientPriceLadder(pd, isOrder = true, deliveryPaid = 0)) continue
                    if (pd.productId <= 0) continue
                    keys.add(lid to pd.productId)
                }
            }
            notes.filterIsInstance<DeliveryNote>().forEach { n ->
                val c = UINKDBInterface.activeDB.getClient(n.ent_id).first ?: return@forEach
                val lid = c.priceListIdForClientPricesFetch()
                for (pd in n.delivery_info) {
                    if (!lineNeedsClientPriceLadder(pd, isOrder = false, deliveryPaid = n.paid)) continue
                    if (pd.productId <= 0) continue
                    keys.add(lid to pd.productId)
                }
            }
            return keys.joinToString(",") { "${it.first}:${it.second}" }
        }

        fun splitListToValueRet(l: List<Note>): Pair<List<Note>, List<Note>> {
            val v = mutableListOf<Note>()
            val r = mutableListOf<Note>()

            l.forEach {
                val type = NoteType.BOTH.convert(it.splittedSize())
                when (type) {

                    NoteType.VALUES -> v.add(it)
                    NoteType.RETURNS -> r.add(it)
                    else -> {
                        v.add(it)
                        r.add(it)
                    }
                }

            }
            return Pair(v, r)
        }

    }

    fun splittedSize(): Int {
        return (if (delivery_info.any { it.value + it.conversion_ratio * it.wrapped_amount > 0 }) 1 else 0) +
                (if (delivery_info.any { it.returns + it.conversion_ratio * it.wrapped_amount_return > 0 }) 1 else 0)
    }

}

@myName("DeliveryNote")
@Serializable
class DeliveryNote(
    override val date: String,
    override val date_issued: String,
    override val agent: String,
    val delivery_id: Int,
    override val ent_id: Int,
    override var active: Int,
    override var delivery_value: String,
    val paid: Int = 0,
    val paid_date: String? = null,
    override var connected_id: Long = -1,
    val order_id: Int = -1,
    override var notes: String = "",
    val pdf: Int = 0,

    var signer: String? = null,
    var digi_sign: Int? = null,
    var digi_photo: Int? = null,
    var date_sign: String? = null,
    override var driver_id: Int? = null,
    override var collector_id: Int? = null,
    override var agent_id: Int = -1,
    override var notes2: String = "",
    override var notes3: String = "", override var history: String = "[]", override var action_time: String = "",
    override var notes4: String = "",
    override var notes5: String = "",
    override var increase_id: Int? = 0,
    /** Forced document fields (business name / id / address / phone) overriding the client. Same JSON shape as [OrderNote.static_data]. */
    val static_data: String? = null,
    /** Parallel to [delivery_value]: priced API JSON per line; `ci` 0 = line `u` (use_price), not utn+paid. */
    @SerialName("delivery_prices") val delivery_prices: String? = null,
) : Note() {
    var isSupNote: Boolean = false
    var ClientStaticData: ClientStaticData? = null

    init {
        delivery_info = ProductDelivery.createFromJson(delivery_value).toMutableList()
        ProductDelivery.mergeDeliveryPricesIntoLines(delivery_info, delivery_prices)
        if (static_data != "null" && !static_data.isNullOrEmpty()) {
            ClientStaticData = Structs.ClientStaticData.createFromJson(static_data)
        }
//        val pricedFromApi = RawPrice.fromDeliveryPricesJsonForClientPriceTable(date, delivery_prices)
//        if (pricedFromApi.isNotEmpty()) {
//            UINKDBInterface.activeDB._updateClientsPrice(pricedFromApi)
//        }
        histroy_info = ProductDelivery.createFromJson(history).toMutableList()
    }

    override val id: Int
        get() = delivery_id

    override fun getStaticData(): ClientStaticData? =
        ClientStaticData ?: getConnectedOrder()?.getStaticData()

    override fun getConnectedDocType(): Int {
        return if (isSupNote) DocType.SUP_DELIVERY_NOTES.state else DocType.DELIVERY_NOTES.state
    }

    fun getConnectedRealDocType(): Int {
        return if (isSupNote) DocType.SUP_DELIVERY_NOTES.state else
            (
                    if (isOnlyReturns()) {
                        DocType.RETURNS.state
                    } else {
                        DocType.DELIVERY_NOTES.state
                    })
    }

    override fun getEnt(client: Boolean): Entity {
        return if (client)
            UINKDBInterface.activeDB.getClient(ent_id).first!!
        else
            UINKDBInterface.activeDB.getSupplier(ent_id).first!!
    }

    override fun signData(): NoteSignHolder? = if (digi_sign == 1 || NoteSignHolder.hasPhoto(digi_photo)) NoteSignHolder(
        id,
        getConnectedDocType(),
        digi_sign ?: 0,
        NoteSignHolder.count(digi_photo),
        signer ?: "",
        date_sign ?: ""
    ) else UINKDBInterface.activeDB.getSignOffline(delivery_id, getConnectedDocType()).first

    override fun digiSign(): Boolean =
        if (digi_sign == 1) true else UINKDBInterface.activeDB.getSignOffline(delivery_id, getConnectedDocType()).first?.digi_sign == 1

    override fun digiPhoto(): Boolean = photoCount() > 0

    override fun photoCount(): Int {
        val n = NoteSignHolder.count(digi_photo)
        if (n > 0) return n
        return NoteSignHolder.count(
            UINKDBInterface.activeDB.getSignOffline(delivery_id, getConnectedDocType()).first?.digi_photo
        )
    }

    override fun isUsed(): Boolean {
        return paid != 0
    }

    fun setSign(NoteSignHolder: NoteSignHolder) {
        digi_sign = NoteSignHolder.digi_sign
        digi_photo = NoteSignHolder.digi_photo
        signer = NoteSignHolder.signer
        date_sign = NoteSignHolder.date
    }



    @myName("getDriver")
    fun getDriver(): Agent? {


        if (driver_id != null && driver_id!! > 0) {
            UINKDBInterface.activeDB.getAgentBy(driver_id!!, null, driver = true).first?.let { return it }
        }
        if (order_id > 0) {
            // Use getEffectiveDriver (not getDriver) to avoid any getDriver → getEffectiveDriver loop on orders.
            getConnectedOrder()?.getEffectiveDriver()?.let { return it }
        }
        return UINKDBInterface.activeDB.getClient(ent_id).first?.getDriverFinder(date)
    }

    @myName("getCollector")
    fun getCollector(): Agent? {
        return collector_id?.let {
            UINKDBInterface.activeDB.getAgentBy(it, null, collector = true).first
        }
    }

    @myName("getConnectedOrder")
    fun getConnectedOrder(): OrderNote? {
        return if (order_id != null)
            UINKDBInterface.activeDB.getClientOrderNotes(order_id = order_id).first.firstOrNull()
        else null
    }

    @myName("getCollectedKarton")
    fun getCollectedKarton(): Map<Int, CollectionObject>? {
        return getConnectedOrder()?.collectionObject
    }
}

@myName("OrderNote")
@Serializable
class OrderNote(
    override val date: String,
    override val date_issued: String,
    override val agent: String,
    val order_id: Int,
    override val ent_id: Int,
    override var active: Int,
    override var delivery_value: String,
    val sup_delivery_info: String,
    override var connected_id: Long = -1,
    override var notes: String = "",
    val category: String = "",
    override var driver_id: Int? = null,
    override var collector_id: Int? = null,
    var signer: String? = null,
    var digi_sign: Int? = null,
    var digi_photo: Int? = null,
    var date_sign: String? = null,
    val ref_id: String? = null,
    override var agent_id: Int = -1,
    override var notes2: String = "",
    override var notes3: String = "",
    override var action_time: String = "",
    override var history: String,
    val collection: String = "{}",
    val freeze: String = "[]",
    override var notes4: String = "",
    override var notes5: String = "",
     var notes6: String = "",
     var notes7: String = "",
     var notes8: String = "",
     var notes9: String = "",
     var notes10: String = "",
    val close_note_id: Int? = null,
    val static_data: String? = null,
    /** Supply/delivery date for the order (תאריך אספקה), distinct from the document [date]. */
    val delivery_date: String? = null,
    val e_agent: String? = null,
    override var increase_id: Int? = 0,
    /** Parallel to [delivery_value]: priced API JSON per line; `ci` 0 = line p>0, else price-flow id. */
    @SerialName("delivery_prices") val delivery_prices: String? = null,
    /** Forced route position for this specific order (like [agent_id] forces the driver). -1 = none, use the client daily position. */
    var force_position: Int = -1,
    /** Optimistic lock. 0 = unknown / old client — server bumps without a conflict check. */
    @myName("row_version")
    var row_version: Int = 0,
) : Note() {
    var freeze_info: MutableList<ProductDelivery> = mutableListOf()
    var ClientStaticData: ClientStaticData? = null
    val sup_delivery_value: MutableList<ProductDelivery> =

        ProductDelivery.createFromJson(sup_delivery_info).toMutableList()

    @myName("orderStatus")
    var orderStatus: OrderStatus = OrderStatus.REGULAR.convert(active)
    val collectionObject = CollectionObject.createFromJson(collection)

    init {
        delivery_info = ProductDelivery.createFromJson(delivery_value).toMutableList()
//        ProductDelivery.mergeDeliveryPricesIntoLines(delivery_info, delivery_prices)
//        val pricedFromApi = RawPrice.fromDeliveryPricesJsonForClientPriceTable(date, delivery_prices)
//        if (pricedFromApi.isNotEmpty()) {
//            UINKDBInterface.activeDB._updateClientsPrice(pricedFromApi)
//        }
        if (date <= "2010-02-01")
            delivery_info.forEach {
                it.price = 0f
                it.discount = 0f
                it.use_price = 0
            }
        histroy_info = ProductDelivery.createFromJson(history).toMutableList()
        if (static_data != "null" && !static_data.isNullOrEmpty()) {
            ClientStaticData = Structs.ClientStaticData.createFromJson(static_data)
        }

        freeze_info = if (freeze == "null") mutableListOf() else ProductDelivery.createFromJson(freeze).toMutableList()
    }

    override fun getConnectedDocType(): Int {
        return DocType.ORDERS.state
    }

    override fun getStaticData(): ClientStaticData? = ClientStaticData

    @myName("getDeliveryDate")
    fun getDeliveryDate(): String? = meaningfulDeliveryDate(delivery_date)

    override fun isActive(): Boolean {
        return active >= 1
    }

    override val id: Int
        get() = order_id

    override fun isUsed(): Boolean {
        return orderStatus == OrderStatus.CLOSED || orderStatus == OrderStatus.ARRIVED
    }

    override fun isOClosed(): Boolean {
        return orderStatus.semi_locked()
    }

    /** True when the order has no ordered amounts (the list "ללא מוצרים" case). */
    fun isBlankOrder(): Boolean =
        delivery_info.none {
            it.value != 0f || it.wrapped_amount != 0f || it.returns != 0f ||
                it.wrapped_amount_return != 0f || it.third_amount != 0f
        }

    @myName("getActiveProducts")
    fun getActiveProducts(date: String? = null): List<ProductDelivery> {
        return delivery_info.filter {
            if (date != null) {
                UINKDBInterface.activeDB.getClient(ent_id).first?.getPrice(it.productId)?.get(date)?.first != 0f
            } else {
                true
            }

        }
    }

    override fun getEnt(client: Boolean): Entity {
        return if (ent_id != -1) UINKDBInterface.activeDB.getClient(ent_id).first!! else EmptyEnt(
            id = -1,
            name = "מלאי",
            agent = getConnectedAgent()
        )
    }

    override fun signData(): NoteSignHolder? = if (digi_sign == 1 || NoteSignHolder.hasPhoto(digi_photo)) NoteSignHolder(
        id,
        10,
        digi_sign ?: 0,
        NoteSignHolder.count(digi_photo),
        signer ?: "",
        date_sign ?: ""
    ) else UINKDBInterface.activeDB.getSignOffline(id, 10).first

    override fun digiSign(): Boolean =
        if (digi_sign == 1) true else UINKDBInterface.activeDB.getSignOffline(id, 10).first?.digi_sign == 1

    override fun digiPhoto(): Boolean = photoCount() > 0

    override fun photoCount(): Int {
        val n = NoteSignHolder.count(digi_photo)
        if (n > 0) return n
        return NoteSignHolder.count(UINKDBInterface.activeDB.getSignOffline(id, 10).first?.digi_photo)
    }

    @myName("getDriver")
    fun getDriver(): Agent? = getEffectiveDriver()

    @myName("getAgentD")
    fun getAgentD(): Agent? {
        return if(agent_id!=-1) UINKDBInterface.activeDB.getAgentBy(agent_id, null,driver = true).first else null
    }

    /** True when this order carries a forced (temporary) driver overriding the client daily driver. */
    @myName("hasForcedDriver")
    fun hasForcedDriver(): Boolean = agent_id != -1

    /** True when this order carries a forced route position overriding the client daily position. */
    @myName("hasForcedPosition")
    fun hasForcedPosition(): Boolean = force_position != -1

    /**
     * Driver this order actually belongs to: the forced driver ([agent_id]) when set,
     * otherwise the note's driver_id from the API, otherwise the client daily driver for [date].
     */
    @myName("getEffectiveDriver")
    fun getEffectiveDriver(): Agent? {
        if (agent_id != -1) return getAgentD()
        if (driver_id != null && driver_id!! > 0) {
            UINKDBInterface.activeDB.getAgentBy(driver_id!!, null, driver = true).first?.let { return it }
        }
        return if (ent_id != -1) UINKDBInterface.activeDB.getClient(ent_id).first?.getDriverFinder(date) else null
    }

    /**
     * Route position of this order: [force_position] when set,
     * otherwise the client daily position for [date].
     */
    @myName("getEffectivePosition")
    fun getEffectivePosition(): Int? {
        if (force_position != -1) return force_position
        return if (ent_id != -1) UINKDBInterface.activeDB.getDateDailyDataFinder(date)?.get(ent_id)?.position else null
    }

    @myName("rowVersion")
    fun rowVersion(): Int = row_version

    @myName("getCollector")
    fun getCollector(): Agent? {
        return collector_id?.let {
            UINKDBInterface.activeDB.getAgentBy(it, null, collector = true).first
        }
    }

    @myName("getKartonString")
    fun getKartonString(): String {
        return collectionObject.map {
            val collectorName = UINKDBInterface.activeDB.getAgentBy(it.key, null, collector = true).first?.user_name
                ?: it.key.toString()
            "$collectorName - ${it.value.box}"
        }.joinToString(", ")
    }

    @myName("isCollected")
    fun isCollected(productId: Int): Boolean {
        UINKDBInterface.activeDB.getClientProduct(productId).first?.let {
            if (collectionObject.containsKey(it.getOrderProduct()?.collector))
                return true
        }
        return false
    }

    @myName("getKartonLst")
    fun getKartonLst(): List<CollectionObject> {
        return collectionObject.values.toList()
    }

    fun setSign(NoteSignHolder: NoteSignHolder) {
        digi_sign = NoteSignHolder.digi_sign
        digi_photo = NoteSignHolder.digi_photo
        signer = NoteSignHolder.signer
        date_sign = NoteSignHolder.date
    }
}

data class baseChangeHolder(val c_id: Int, val p_id: Int, val amount: Float, val day: Int? = null)

@Serializable
data class PriceOfferProduct(
    val id: String,
    val v: String,
    val price: String,
    val text: String = "",
) {
    fun productId(): Int = id.toIntOrNull() ?: 0
    fun quantity(): Float = v.toFloatOrNull() ?: 0f
    fun priceValue(): Float = price.toFloatOrNull() ?: 0f
    fun total(): Float = quantity() * priceValue()
    fun isHandWritten(): Boolean = text.isNotEmpty()
}

@myName("PriceOffer")
@Serializable
data class PriceOffer(
    val number: Int,
    val id: Int = 0,
    val client_id: Int = -1,
    val date: String,
    val products: String,
    val company_id: Int,
    val name: String,
    val created: String = "",
) {
    val products_list: List<PriceOfferProduct> by lazy {
        try {
            kotlinx.serialization.json.Json { ignoreUnknownKeys = true }
                .decodeFromString(kotlinx.serialization.builtins.ListSerializer(PriceOfferProduct.serializer()), products)
        } catch (e: Exception) {
            emptyList()
        }
    }
}